import React, { useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, TextField,
} from '@mui/material';
import { ActivityService } from '../../services/activity.service';
import {
  ActivityOwnerType, ActivityType, ACTIVITY_TYPE_LABELS,
} from '../../interfaces/crm/crm.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

interface LogActivityDialogProps {
  open: boolean;
  ownerType: ActivityOwnerType;
  ownerId: string;
  onClose: () => void;
  onLogged: () => void;
}

const ACTIVITY_TYPES: ActivityType[] = ['CALL', 'EMAIL', 'MEETING', 'NOTE'];

const LogActivityDialog: React.FC<LogActivityDialogProps> = ({
  open, ownerType, ownerId, onClose, onLogged,
}) => {
  const toast = useToast();
  const [type, setType] = useState<ActivityType>('CALL');
  const [note, setNote] = useState('');
  const [occurredAt, setOccurredAt] = useState(''); // empty → backend defaults to now()
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setType('CALL');
    setNote('');
    setOccurredAt('');
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (note.trim() === '') return;
    setSaving(true);
    try {
      const institutionId = AuthService.getSelectedInstitution();
      await ActivityService.create({
        ownerType,
        ownerId,
        type,
        note: note.trim(),
        // Convert the datetime-local value (no timezone) to an ISO instant; omit if blank.
        occurredAt: occurredAt ? new Date(occurredAt).toISOString() : undefined,
        institutionId,
      });
      toast.showSuccess('Activity logged');
      reset();
      onLogged();
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to log activity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" data-help-page="crm.activity.page">
      <DialogTitle>Log Activity</DialogTitle>
      <DialogContent>
        <Box data-help-section="crm.activity.section.log" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            data-help="crm.activity.field.type"
            select
            label="Type"
            value={type}
            onChange={e => setType(e.target.value as ActivityType)}
          >
            {ACTIVITY_TYPES.map(t => (
              <MenuItem key={t} value={t}>{ACTIVITY_TYPE_LABELS[t]}</MenuItem>
            ))}
          </TextField>
          <TextField
            data-help="crm.activity.field.note"
            label="Note"
            value={note}
            onChange={e => setNote(e.target.value)}
            multiline
            minRows={3}
            required
          />
          <TextField
            data-help="crm.activity.field.occurredAt"
            label="Occurred At"
            type="datetime-local"
            value={occurredAt}
            onChange={e => setOccurredAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Leave blank for now"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-help="crm.activity.cancel" onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          data-help="crm.activity.save"
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || note.trim() === ''}
        >
          {saving ? <CircularProgress size={20} /> : 'Log Activity'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogActivityDialog;
